import { CheckIcon } from "@xola/icons";
import { Table } from "@xola/ui-kit";
import clsx from "clsx";
import { QuantitySpinner } from "common";
import React, { useMemo } from "react";
import { LineItem, Purchase } from "types";
import {
    getFulfilledQuantity,
    getMerchandisePurchaseItems,
    getMerchandiseWareLineItems,
} from "@/models/MerchandisePurchaseItem";

interface MerchandiseFulfillmentTableProps {
    purchase: Purchase;
    toFulfillMap: Record<string, number>;
    errorByLineItemId?: Record<string, string[]>;
    successLineItemIds?: string[];
    onToFulfillChange: (lineItemId: string, value: number) => void;
}

export const MerchandiseFulfillmentTable = ({
    purchase,
    toFulfillMap,
    errorByLineItemId = {},
    successLineItemIds = [],
    onToFulfillChange,
}: MerchandiseFulfillmentTableProps) => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const successLineItemIdSet = useMemo(() => new Set(successLineItemIds), [successLineItemIds]);

    return (
        <div className="overflow-hidden rounded-lg border border-gray-lighter">
            <Table className="w-full table-fixed border-none">
                <Table.Head>
                    <Table.Row>
                        <Table.Header className="w-32 text-left">Item</Table.Header>
                        <Table.Header className="w-16 text-center">Purchased</Table.Header>
                        <Table.Header className="w-16 text-center">Fulfilled</Table.Header>
                        <Table.Header className="w-24 text-left">To Fulfill</Table.Header>
                    </Table.Row>
                </Table.Head>
            </Table>

            <div className="max-h-80 overflow-y-auto">
                <Table className="w-full table-fixed border-none">
                    <Table.Body>
                        {merchandiseItems.map((item) => (
                            <React.Fragment key={item.id}>
                                {getMerchandiseWareLineItems(item).map((lineItem: LineItem) => {
                                    const fulfilled = getFulfilledQuantity(item, lineItem.template?.id);
                                    const ordered = lineItem.quantity ?? 0;
                                    if (ordered <= 0) return null;
                                    const toFulfill = toFulfillMap[lineItem.id] ?? 0;
                                    const remaining = ordered - fulfilled;
                                    const errorMessages = errorByLineItemId[lineItem.id] ?? [];
                                    const hasError = errorMessages.length > 0;
                                    const isSuccess = successLineItemIdSet.has(lineItem.id);
                                    return (
                                        <React.Fragment key={lineItem.id}>
                                            <Table.Row>
                                                <Table.Cell className="w-32">
                                                    <div
                                                        className={clsx("whitespace-normal break-all font-bold", {
                                                            "text-danger": hasError,
                                                        })}
                                                    >
                                                        {item.name}
                                                    </div>
                                                    <div
                                                        className={clsx("whitespace-normal break-all text-sm", {
                                                            "text-danger": hasError,
                                                            "text-gray-dark": !hasError,
                                                        })}
                                                        data-testid="variant-item-name"
                                                    >
                                                        {lineItem.name}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell
                                                    className="w-16 text-center"
                                                    data-testid="variant-item-purchased"
                                                >
                                                    {ordered}
                                                </Table.Cell>
                                                <Table.Cell
                                                    className="w-16 text-center"
                                                    data-testid="variant-item-fulfilled"
                                                >
                                                    {fulfilled}
                                                </Table.Cell>
                                                <Table.Cell className="w-24 pr-3 text-left">
                                                    <div className="flex items-center space-x-2">
                                                        <QuantitySpinner
                                                            allowEditManually
                                                            value={toFulfill}
                                                            min={0}
                                                            minErrorMessage="Cannot fulfill a negative quantity"
                                                            max={remaining}
                                                            error={hasError ? "true" : undefined}
                                                            onChange={(value) => onToFulfillChange(lineItem.id, value)}
                                                        />
                                                        <div className="flex h-6 w-6 items-center justify-center">
                                                            {isSuccess && !hasError ? (
                                                                <div data-testid="fulfillment-success-tick">
                                                                    <CheckIcon size="small" className="text-success" />
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                            {hasError && (
                                                <Table.Row>
                                                    <Table.Cell colSpan={4} className="!px-1 !pb-2 !pt-0">
                                                        <div className="space-y-1 px-2 py-1">
                                                            {errorMessages.map((message) => (
                                                                <div key={message} className="text-sm text-danger">
                                                                    {message}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </div>
    );
};
